import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dispatch, RootState } from "../../store";
import DataTable, { TableColumn } from "react-data-table-component";
import { Snapshot } from "../../types";
import Button from "../../components/styled/Button";
import { ProcessingContainer, WrappedRow } from "../Processing/styled";
import WalletForm from "../../components/WalletForm";
import FormSteps from "../../components/FormSteps";
import { Divider } from "../../components/styled/Divider";
import InfoBlock from "./InfoBlock";
import { Row } from "../../components/styled/Flex";
import AddressSearch from "../../components/AddressSearch";
import Checkbox from "../../components/styled/Checkbox";
import { FormLabel } from "../../components/styled/Form";
import SnapshotDownload from "../../components/SnapshotDownload";

const columns: TableColumn<Snapshot>[] = [
  {
    name: "Wallet",
    selector: (row) => row.wallet,
    sortable: true,
  },
  {
    name: "Balance",
    selector: (row) => +row.balance,
    sortable: true,
  },
  {
    name: "Type",
    selector: (row) => row.type,
    sortable: true,
  },
];

const customDataTableStyles = {
  pagination: {
    style: {
      display: "flex",
      justifyContent: "space-between",
    },
  },
};

const SnapshotReview = () => {
  const navigator = useNavigate();
  const snapshotData = useSelector(
    (state: RootState) => state.snapshotModel.snapshotData
  );

  const currentSnapshotId = useSelector(
    (state: RootState) => state.snapshotModel.currentSnapshotId
  );
  const [data, setData] = useState(snapshotData);
  const [filtedData, setFiltedData] = useState<Snapshot[]>([]);
  // row selection
  const [selectedRows, setSelectedRows] = useState<Snapshot[]>([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  // search stuff
  const [searchAddress, setSearchAddress] = useState("");
  // checkboxes
  const [toggledContractFilter, setToggledContractFilter] = useState(false);
  const [toggleBalanceTrim, setToggleBalanceTrim] = useState(false);

  const dispatch = useDispatch<Dispatch>();

  const handleRowSelected = useCallback((state: any) => {
    setSelectedRows(state.selectedRows);
  }, []);

  const handleDelete = useCallback(() => {
    if (!data) return;
    if (
      window.confirm(
        `Are you sure you want to delete:\r ${selectedRows.map(
          (r) => r.wallet
        )}?`
      )
    ) {
      setToggleCleared(!toggleCleared);
      const selectedRowsToDelete = [...selectedRows];
      const selectedRowsToDeleteIds = selectedRowsToDelete.map((r) =>
        r.wallet.toLowerCase()
      );

      const newData = data.filter(
        (r) => !selectedRowsToDeleteIds.includes(r.wallet.toLowerCase())
      );
      setData(newData);
      setFiltedData(newData);
      setSelectedRows([]);
    }
  }, [data, selectedRows, toggleCleared, filtedData]);

  const restartSnapshot = () => {
    navigator("/token-submit");
    dispatch.snapshotModel.resetState();
  };

  useEffect(() => {
    dispatch.multistepModel.setCurrentStep(3);
    return () => {
      dispatch.multistepModel.setCurrentStep(2);
    };
  }, [dispatch]);

  const filterContracts = (type: string) => {
    if (!toggledContractFilter) return true;
    return type.toLowerCase() !== "contract";
  };

  const filterBalance = (balance: string) => {
    if (!toggleBalanceTrim) return true;
    return +balance > 0.0001;
  };

  const filterSearch = (snapshot: Snapshot, searchTerm: string) => {
    if (!searchTerm) return true;
    return snapshot.wallet.toLowerCase().includes(searchTerm.toLowerCase());
  };

  useEffect(() => {
    if (!data) return;
    const newData = data;

    const filteredData = newData.filter((row) => {
      return filterContracts(row.type) && filterBalance(row.balance);
    });

    const searchFilter = filteredData.filter((row) => {
      return filterSearch(row, searchAddress);
    });
    setFiltedData(searchFilter);
    // eslint-disable-next-line
  }, [data, toggledContractFilter, toggleBalanceTrim, searchAddress]);

  const handleNext = useCallback(() => {
    const snapshotDataToSet = filtedData.length > 0 ? filtedData : data;
    dispatch.snapshotModel.setSnapshotData(snapshotDataToSet);
    navigator("/tx-confirm");
  }, [navigator, data, filtedData, dispatch.snapshotModel]);

  return (
    <ProcessingContainer>
      <WalletForm marginTop="50px" marginBottom="50px">
        <FormSteps />
        <Divider />
        <InfoBlock />
        <Divider />

        <Row justify="space-between">
          <FormLabel>
            <Checkbox
              checked={toggledContractFilter}
              onChange={() => setToggledContractFilter(!toggledContractFilter)}
            />
            Remove contract addresses
          </FormLabel>

          <FormLabel>
            <Checkbox
              checked={toggleBalanceTrim}
              onChange={() => setToggleBalanceTrim(!toggleBalanceTrim)}
            />
            Trim low balances
          </FormLabel>
        </Row>
        <Row
          style={{
            marginTop: "10px",
            marginBottom: "10px",
          }}
        >
          <AddressSearch
            value={searchAddress}
            onChangeText={(e) => {
              setSearchAddress(e.target.value);
            }}
          />
        </Row>
        {selectedRows.length > 0 && (
          <Row>
            <Button key="delete" onClick={handleDelete}>
              Delete
            </Button>
          </Row>
        )}
        <DataTable
          columns={columns}
          data={filtedData.length > 0 ? filtedData : data!}
          selectableRows
          onSelectedRowsChange={handleRowSelected}
          clearSelectedRows={toggleCleared}
          pagination
          fixedHeader
          fixedHeaderScrollHeight="300px"
          customStyles={customDataTableStyles}
        />

        {snapshotData && snapshotData.length > 0 && (
          <WrappedRow>
            <SnapshotDownload
              snapshotData={snapshotData}
              id={currentSnapshotId}
            />
          </WrappedRow>
        )}

        <WrappedRow justify="space-between">
          <Button onClick={restartSnapshot}>Try again</Button>
          <Button onClick={handleNext}>Next</Button>
        </WrappedRow>
      </WalletForm>
    </ProcessingContainer>
  );
};

export default SnapshotReview;
